import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetTypes } from "src/apis/TypeApis";
import { GetLocations } from "src/apis/locationApis";

const CACHE_EXPIRY = 30 * 60;

interface CommonFilterData {
  locations: any[];
  types: any[];
}

export interface CommonFiltersState {
  data: CommonFilterData | null;
  lastFetched: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: CommonFiltersState = {
  data: null,
  lastFetched: null,
  loading: false,
  error: null,
};

export const fetchCommonFilters = createAsyncThunk<
  CommonFilterData,
  { clientId: string | null },
  { rejectValue: string }
>(
  "commonFilters/fetchCommonFilters",
  async ({ clientId }, { getState, rejectWithValue }) => {
    const state = (getState() as any).commonFilters;
    const now = Math.floor(Date.now() / 1000);

    if (
      state.data &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_EXPIRY
    ) {
      return state.data;
    }

    if (!clientId) {
      return rejectWithValue("Client ID is required");
    }

    try {
      const [typesResp, locationsResp] = await Promise.all([
        GetTypes(),
        GetLocations(clientId),
      ]);

      const filterData: CommonFilterData = {
        types: typesResp.data || [],
        locations: locationsResp.data?.records || [],
      };

      return filterData;
    } catch (error) {
      console.error("Error fetching common filters:", error);
      return rejectWithValue("Failed to fetch filter data");
    }
  }
);

const commonFiltersSlice = createSlice({
  name: "commonFilters",
  initialState,
  reducers: {
    clearCommonFilters: (state) => {
      state.data = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommonFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCommonFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterData>) => {
          state.loading = false;
          state.data = action.payload;
          state.lastFetched = Math.floor(Date.now() / 1000);
          state.error = null;
        }
      )
      .addCase(fetchCommonFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { clearCommonFilters } = commonFiltersSlice.actions;
export default commonFiltersSlice.reducer;

export const selectCommonFilterData = (state: any) => state.commonFilters.data;
export const selectCommonFilterLoading = (state: any) =>
  state.commonFilters.loading;
export const selectCommonLocations = (state: any) =>
  state.commonFilters.data?.locations || [];
export const selectCommonTypes = (state: any) =>
  state.commonFilters.data?.types || [];
